import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { TopNav } from "@/components/TopNav";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { User as UserIcon, Mail, ShieldCheck, KeyRound, UserCog, LogOut, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  fr: {
    title: "Mon profil",
    account: "Compte",
    subscription: "Abonnement",
    active: "Abonnement actif",
    inactive: "Aucun abonnement",
    manage: "Gérer / résilier via Stripe",
    subscribe: "S'abonner",
    security: "Sécurité",
    changePwd: "Changer mon mot de passe",
    currentPwd: "Mot de passe actuel",
    newPwd: "Nouveau mot de passe (min. 8)",
    save: "Enregistrer",
    saved: "Mot de passe mis à jour",
    signout: "Se déconnecter",
    danger: "Zone dangereuse",
    deleteBtn: "Supprimer mon compte",
    confirm: "Cette action est irréversible. Toutes vos données seront supprimées. Tapez SUPPRIMER pour confirmer.",
    confirmWord: "SUPPRIMER",
    yourPwd: "Votre mot de passe",
    deleteFinal: "Supprimer définitivement",
    cancel: "Annuler",
    googleAccount: "Compte connecté via Google — pas de mot de passe à gérer.",
    genericError: "Une erreur est survenue.",
  },
  en: {
    title: "My profile",
    account: "Account",
    subscription: "Subscription",
    active: "Active subscription",
    inactive: "No subscription",
    manage: "Manage / cancel via Stripe",
    subscribe: "Subscribe",
    security: "Security",
    changePwd: "Change my password",
    currentPwd: "Current password",
    newPwd: "New password (min. 8)",
    save: "Save",
    saved: "Password updated",
    signout: "Sign out",
    danger: "Danger zone",
    deleteBtn: "Delete my account",
    confirm: "This action is irreversible. All your data will be deleted. Type DELETE to confirm.",
    confirmWord: "DELETE",
    yourPwd: "Your password",
    deleteFinal: "Delete permanently",
    cancel: "Cancel",
    googleAccount: "Account signed in with Google — no password to manage.",
    genericError: "An error occurred.",
  },
};

function formatDetail(d, fallback) {
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
}

const ProfilePage = () => {
  const { lang } = useLang();
  const { user, loading, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const t = T[lang];

  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "" });
  const [pwdMsg, setPwdMsg] = useState({ type: "", text: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  const [portalBusy, setPortalBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmWord, setConfirmWord] = useState("");
  const [deletePwd, setDeletePwd] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  const openPortal = async () => {
    if (!user?.subscription_session_id) return;
    // Admin-granted subscriptions have no real Stripe session.
    if (user.subscription_session_id.startsWith("cs_admin_grant_")) {
      alert(t.lang === "fr" || document.documentElement.lang === "fr"
        ? "Abonnement offert par l'équipe GEM PRICE. Pour toute modification, contactez gemprice@gemprice.org."
        : "Complimentary subscription granted by GEM PRICE. For any change, contact gemprice@gemprice.org.");
      return;
    }
    setPortalBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/portal`,
        { session_id: user.subscription_session_id, return_url: window.location.href },
        { withCredentials: true });
      window.location.href = data.portal_url;
    } catch {
      setPortalBusy(false);
      alert(t.genericError);
    }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    setPwdBusy(true); setPwdMsg({ type: "", text: "" });
    try {
      await axios.post(`${API}/auth/change-password`, pwdForm, { withCredentials: true });
      setPwdMsg({ type: "ok", text: t.saved });
      setPwdForm({ current_password: "", new_password: "" });
    } catch (err) {
      setPwdMsg({ type: "err", text: formatDetail(err?.response?.data?.detail, t.genericError) });
    } finally {
      setPwdBusy(false);
    }
  };

  const doDelete = async () => {
    setDeleteBusy(true); setDeleteErr("");
    try {
      await axios.post(`${API}/auth/delete-account`,
        { password: deletePwd || null }, { withCredentials: true });
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      setDeleteBusy(false);
      setDeleteErr(formatDetail(err?.response?.data?.detail, t.genericError));
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#050811] text-[#E2CFA4] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#C1A661]" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050811] text-[#F8F5EE] flex flex-col">
      <SEO title={t.title} path="/profile" noindex />
      <TopNav />
      <main className="flex-1 px-6 md:px-14 py-14" data-testid="profile-page">
        <div className="max-w-2xl mx-auto">
          <h1 className="font-serif text-4xl md:text-5xl tracking-[0.06em] text-[#E2CFA4] mb-8">{t.title}</h1>

          {/* Account */}
          <section className="mb-8 border border-[#C1A661]/25 rounded-sm p-5 bg-[#0A0F1F]/60" data-testid="profile-account-section">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C1A661] mb-3">{t.account}</div>
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt="" width="48" height="48" className="rounded-full" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-12 h-12 rounded-full border border-[#C1A661]/30 flex items-center justify-center">
                  <UserIcon size={20} className="text-[#C1A661]" />
                </div>
              )}
              <div className="min-w-0">
                {user.name && <div className="text-[#E2CFA4] text-sm font-medium truncate">{user.name}</div>}
                <div className="flex items-center gap-2 text-[#A1A7B3] text-sm">
                  <Mail size={13} className="text-[#C1A661]" />
                  <span className="truncate" data-testid="profile-email">{user.email}</span>
                </div>
              </div>
            </div>
          </section>

          {/* Subscription */}
          <section className="mb-8 border border-[#C1A661]/25 rounded-sm p-5 bg-[#0A0F1F]/60" data-testid="profile-subscription-section">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C1A661] mb-3">{t.subscription}</div>
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck size={18} className={user.subscription_active ? "text-[#C1A661]" : "text-[#8A8F99]"} />
              <div className={`text-sm ${user.subscription_active ? "text-[#E2CFA4]" : "text-[#8A8F99]"}`} data-testid="profile-subscription-status">
                {user.subscription_active ? t.active : t.inactive}
              </div>
            </div>
            {user.subscription_active ? (
              <button
                onClick={openPortal}
                disabled={portalBusy}
                data-testid="profile-manage-subscription"
                className="inline-flex items-center gap-2 rounded-full bg-[#C1A661] hover:bg-[#E2CFA4] disabled:opacity-60 text-[#050B1E] px-4 py-2 text-[11px] tracking-[0.2em] uppercase font-semibold"
              >
                {portalBusy ? <Loader2 size={13} className="animate-spin" /> : <UserCog size={13} />}
                {t.manage}
              </button>
            ) : (
              <button
                onClick={() => navigate("/")}
                data-testid="profile-subscribe-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] px-4 py-2 text-[11px] tracking-[0.2em] uppercase text-[#E2CFA4]"
              >
                {t.subscribe}
              </button>
            )}
          </section>

          {/* Security */}
          <section className="mb-8 border border-[#C1A661]/25 rounded-sm p-5 bg-[#0A0F1F]/60" data-testid="profile-security-section">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C1A661] mb-3">{t.security}</div>
            {user.has_password ? (
              <form onSubmit={submitPwd} className="space-y-3" data-testid="profile-password-form">
                <div className="flex items-center gap-2 border border-[#C1A661]/25 rounded-sm px-3 py-2 bg-[#050811]">
                  <KeyRound size={14} className="text-[#C1A661]" />
                  <input
                    type="password" required minLength={8} placeholder={t.currentPwd}
                    autoComplete="current-password"
                    value={pwdForm.current_password}
                    onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                    data-testid="profile-current-password"
                    className="flex-1 bg-transparent outline-none text-sm text-[#E2CFA4] placeholder:text-[#8A8F99]"
                  />
                </div>
                <div className="flex items-center gap-2 border border-[#C1A661]/25 rounded-sm px-3 py-2 bg-[#050811]">
                  <KeyRound size={14} className="text-[#C1A661]" />
                  <input
                    type="password" required minLength={8} placeholder={t.newPwd}
                    autoComplete="new-password"
                    value={pwdForm.new_password}
                    onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                    data-testid="profile-new-password"
                    className="flex-1 bg-transparent outline-none text-sm text-[#E2CFA4] placeholder:text-[#8A8F99]"
                  />
                </div>
                {pwdMsg.text && (
                  <div
                    data-testid="profile-password-msg"
                    className={`text-[11px] rounded-sm px-3 py-2 border ${
                      pwdMsg.type === "ok"
                        ? "text-[#C1A661] bg-[#C1A661]/10 border-[#C1A661]/25"
                        : "text-[#F4A3A3] bg-[#F4A3A3]/10 border-[#F4A3A3]/25"
                    }`}
                  >
                    {pwdMsg.type === "ok" && <CheckCircle2 size={12} className="inline mr-1" />}
                    {pwdMsg.text}
                  </div>
                )}
                <button
                  type="submit" disabled={pwdBusy}
                  data-testid="profile-password-submit"
                  className="w-full flex items-center justify-center gap-2 rounded-sm bg-[#C1A661] hover:bg-[#E2CFA4] disabled:opacity-60 text-[#050B1E] font-medium py-2 px-4 text-sm"
                >
                  {pwdBusy && <Loader2 size={13} className="animate-spin" />}
                  {t.save}
                </button>
              </form>
            ) : (
              <p className="text-[12px] text-[#A1A7B3] italic">{t.googleAccount}</p>
            )}

            <div className="mt-5 pt-4 border-t border-[#C1A661]/10">
              <button
                onClick={async () => { await logout(); navigate("/", { replace: true }); }}
                data-testid="profile-signout-btn"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#A1A7B3] hover:text-[#E2CFA4]"
              >
                <LogOut size={13} /> {t.signout}
              </button>
            </div>
          </section>

          {/* Danger zone */}
          <section className="border border-[#F4A3A3]/25 rounded-sm p-5 bg-[#F4A3A3]/5" data-testid="profile-danger-section">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#F4A3A3] mb-3">{t.danger}</div>
            {!deleteOpen ? (
              <button
                onClick={() => setDeleteOpen(true)}
                data-testid="profile-delete-btn"
                className="inline-flex items-center gap-2 rounded-sm border border-[#F4A3A3]/40 hover:bg-[#F4A3A3]/10 text-[#F4A3A3] px-4 py-2 text-[11px] tracking-[0.2em] uppercase"
              >
                <Trash2 size={13} /> {t.deleteBtn}
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-[12px] text-[#A1A7B3]">{t.confirm}</p>
                <input
                  type="text" placeholder={t.confirmWord}
                  value={confirmWord}
                  onChange={(e) => setConfirmWord(e.target.value)}
                  data-testid="profile-delete-confirm-word"
                  className="w-full border border-[#F4A3A3]/25 rounded-sm px-3 py-2 bg-[#050811] text-sm text-[#E2CFA4] placeholder:text-[#8A8F99] outline-none"
                />
                {user.has_password && (
                  <input
                    type="password" placeholder={t.yourPwd}
                    value={deletePwd}
                    onChange={(e) => setDeletePwd(e.target.value)}
                    data-testid="profile-delete-password"
                    className="w-full border border-[#F4A3A3]/25 rounded-sm px-3 py-2 bg-[#050811] text-sm text-[#E2CFA4] placeholder:text-[#8A8F99] outline-none"
                  />
                )}
                {deleteErr && <div className="text-[11px] text-[#F4A3A3]">{deleteErr}</div>}
                <div className="flex gap-2">
                  <button
                    onClick={doDelete}
                    disabled={confirmWord !== t.confirmWord || deleteBusy}
                    data-testid="profile-delete-final-btn"
                    className="rounded-sm bg-[#F4A3A3] hover:bg-[#F17272] disabled:opacity-40 text-[#050B1E] px-4 py-2 text-[11px] tracking-[0.2em] uppercase font-semibold"
                  >
                    {deleteBusy && <Loader2 size={13} className="animate-spin inline mr-1" />}
                    {t.deleteFinal}
                  </button>
                  <button
                    onClick={() => { setDeleteOpen(false); setConfirmWord(""); setDeletePwd(""); setDeleteErr(""); }}
                    data-testid="profile-delete-cancel-btn"
                    className="text-[11px] tracking-[0.2em] uppercase text-[#A1A7B3] hover:text-[#E2CFA4] px-3"
                  >
                    {t.cancel}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProfilePage;
